import React, { useEffect, useState } from 'react';
import './Home.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3500';

interface SubDetail {
  value: number | null;
  source: string | null;
}

interface DetailRow {
  name: string;
  value: number | null;
  source: string | null;
  column?: string;
  after?: string;
  sign?: number;
  sub_details?: SubDetail[];
}

interface ReportRow {
  name: string;
  value: number | null;
  source: string | null;
  bold?: boolean;
  color?: string;
  details?: DetailRow[];
}

interface ReportSection {
  title: string | null;
  rows: ReportRow[];
}

interface ReportTable {
  name: string;
  sections: ReportSection[];
}

interface ConfigTable {
  name: string;
  sections: { title: string | null; rows: ConfigRow[] }[];
}

interface ConfigRow {
  name: string;
  type: string;
  lookups?: ConfigLookup[];
  [key: string]: any;
}

interface ConfigLookup {
  type?: string;
  file: string;
  account: string;
  group: string;
  sub: string;
  sign?: number;
  after?: string;
  before?: string;
  sum_all?: boolean;
  limit?: number;
  after_verify?: string[];
  account_col?: number;
}

interface EditingRow {
  ti: number;
  si: number;
  ri: number;
  name: string;
  lookups: ConfigLookup[];
}

interface AccountInfo {
  name: string;
  col: number;
  row: number;
}

export default function Home({ apiKey }: { apiKey: string }) {
  const [tables, setTables] = useState<ReportTable[]>([]);
  const [warnings, setWarnings] = useState<{ row: string; detail?: string; source: string; table: string }[]>([]);
  // Сходимость расшифровок со строками СГД — считается на бэкенде по правилу из конфига.
  const [checks, setChecks] = useState<{ table: string; anchor: string[]; anchor_value: number;
    table_sum: number; diff: number; ok: boolean; incomplete: boolean }[]>([]);
  const [months, setMonths] = useState<number | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [openTables, setOpenTables] = useState<Set<number>>(() => {
    try {
      const saved = localStorage.getItem('homeDefaultOpen');
      if (saved) return new Set(JSON.parse(saved) as number[]);
    } catch {}
    return new Set([2]);
  });
  const [clusters, setClusters] = useState<string[]>([]);
  const [selectedCluster, setSelectedCluster] = useState<string>(localStorage.getItem('defaultCluster') || 'default');
  const [configs, setConfigs] = useState<string[]>([]);
  const [selectedConfig, setSelectedConfig] = useState<string>(localStorage.getItem('defaultConfig') || 'dd');
  const [loading, setLoading] = useState(false);

  // Config editor state
  const [configData, setConfigData] = useState<ConfigTable[] | null>(null);
  const [isCustom, setIsCustom] = useState(false);
  const [editingRow, setEditingRow] = useState<EditingRow | null>(null);
  const [accounts, setAccounts] = useState<AccountInfo[]>([]);
  const [savingLookups, setSavingLookups] = useState(false);
  // New lookup form
  const [newLkFile, setNewLkFile] = useState('');
  const [newLkAccount, setNewLkAccount] = useState('');
  const [newLkGroup, setNewLkGroup] = useState('Оборот за период');
  const [newLkSub, setNewLkSub] = useState('Дебет');
  const [newLkSign, setNewLkSign] = useState(1);
  const [newLkAfter, setNewLkAfter] = useState('');

  useEffect(() => {
    const headers = { 'X-API-Key': apiKey };
    fetch(`${API_URL}/clusters`, { headers })
      .then((res) => res.json())
      .then((data) => {
        const list = data.clusters || [];
        setClusters(list);
        if (list.length > 0 && !list.includes(selectedCluster)) {
          setSelectedCluster(list[0]);
        }
      })
      .catch(() => {});
    fetch(`${API_URL}/configs`, { headers })
      .then((res) => res.json())
      .then((data) => {
        const list = data.configs || [];
        setConfigs(list);
        if (list.length > 0 && !list.includes(selectedConfig)) {
          setSelectedConfig(list[0]);
        }
      })
      .catch(() => {});
    // Длительность отчёта — одна на всё приложение, а влияет на вычет ЗП в каждой строке.
    // Показываем её рядом с отчётом, чтобы забытое значение было видно, а не портило цифры молча.
    fetch(`${API_URL}/overall/settings`, { headers })
      .then((res) => res.json())
      .then((data) => setMonths(data.settings?.months ?? null))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedCluster || !selectedConfig) return;
    setLoading(true);
    fetch(`${API_URL}/report?cluster=${encodeURIComponent(selectedCluster)}&config=${encodeURIComponent(selectedConfig)}`, { headers: { 'X-API-Key': apiKey } })
      .then((res) => res.json())
      .then((data) => { setTables(data.tables || []); setWarnings(data.warnings || []); setChecks(data.checks || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [selectedCluster, selectedConfig]);

  // Load config structure when config changes
  useEffect(() => {
    if (!selectedConfig) return;
    fetch(`${API_URL}/config/${selectedConfig}`, { headers: { 'X-API-Key': apiKey } })
      .then((r) => r.json())
      .then((data) => { setConfigData(data.tables); setIsCustom(data.is_custom); })
      .catch(() => setConfigData(null));
  }, [selectedConfig, tables]); // reload after report refresh

  const openRowEditor = (ti: number, si: number, ri: number, row: ConfigRow) => {
    setEditingRow({ ti, si, ri, name: row.name, lookups: [...(row.lookups || [])] });
    setNewLkFile('');
    setNewLkAccount('');
    setNewLkAfter('');
    // Load accounts for current cluster + first file in lookups
    if (row.lookups && row.lookups.length > 0) {
      loadAccounts(row.lookups[0].file);
    }
  };

  const loadAccounts = (fileKey: string) => {
    if (!selectedCluster || !fileKey) return;
    fetch(`${API_URL}/file-accounts?cluster=${encodeURIComponent(selectedCluster)}&file_key=${encodeURIComponent(fileKey)}`, { headers: { 'X-API-Key': apiKey } })
      .then((r) => r.json())
      .then((data) => setAccounts(data.accounts || []))
      .catch(() => setAccounts([]));
  };

  const removeLookup = (idx: number) => {
    if (!editingRow) return;
    setEditingRow({ ...editingRow, lookups: editingRow.lookups.filter((_, i) => i !== idx) });
  };

  const addLookup = () => {
    if (!editingRow || !newLkFile || !newLkAccount) return;
    const lk: ConfigLookup = {
      type: 'lookup', file: newLkFile, account: newLkAccount,
      group: newLkGroup, sub: newLkSub, sign: newLkSign,
    };
    if (newLkAfter) lk.after = newLkAfter;
    setEditingRow({ ...editingRow, lookups: [...editingRow.lookups, lk] });
    setNewLkAccount('');
    setNewLkAfter('');
  };

  const saveLookups = () => {
    if (!editingRow) return;
    setSavingLookups(true);
    const nonStaticLookups = editingRow.lookups.filter((l) => l.type !== 'static');
    fetch(`${API_URL}/config/${selectedConfig}/table/${editingRow.ti}/section/${editingRow.si}/row/${editingRow.ri}/lookups`, {
      method: 'PUT',
      headers: { 'X-API-Key': apiKey, 'Content-Type': 'application/json' },
      body: JSON.stringify(nonStaticLookups),
    })
      .then(() => {
        setEditingRow(null);
        // Refresh report
        setLoading(true);
        return fetch(`${API_URL}/report?cluster=${encodeURIComponent(selectedCluster)}&config=${encodeURIComponent(selectedConfig)}`, { headers: { 'X-API-Key': apiKey } });
      })
      .then((r) => r.json())
      .then((data) => { setTables(data.tables || []); setWarnings(data.warnings || []); setChecks(data.checks || []); })
      .catch(() => {})
      .finally(() => { setSavingLookups(false); setLoading(false); });
  };

  const resetConfig = () => {
    if (!window.confirm('Сбросить к исходному конфигу? Все пользовательские изменения будут потеряны.')) return;
    fetch(`${API_URL}/config/${selectedConfig}/reset`, {
      method: 'POST',
      headers: { 'X-API-Key': apiKey },
    })
      .then(() => {
        setLoading(true);
        return fetch(`${API_URL}/report?cluster=${encodeURIComponent(selectedCluster)}&config=${encodeURIComponent(selectedConfig)}`, { headers: { 'X-API-Key': apiKey } });
      })
      .then((r) => r.json())
      .then((data) => { setTables(data.tables || []); setWarnings(data.warnings || []); setChecks(data.checks || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  // Find config row type by position
  const getConfigRowType = (ti: number, rowIdx: number): string | undefined => {
    if (!configData || ti >= configData.length) return undefined;
    let idx = 0;
    for (const section of configData[ti].sections) {
      for (const row of section.rows) {
        if (idx === rowIdx) return row.type;
        idx++;
      }
    }
    return undefined;
  };

  const getConfigRowInfo = (ti: number, rowIdx: number): { si: number; ri: number; row: ConfigRow } | undefined => {
    if (!configData || ti >= configData.length) return undefined;
    let idx = 0;
    for (let si = 0; si < configData[ti].sections.length; si++) {
      for (let ri = 0; ri < configData[ti].sections[si].rows.length; ri++) {
        if (idx === rowIdx) return { si, ri, row: configData[ti].sections[si].rows[ri] };
        idx++;
      }
    }
    return undefined;
  };

  const formatNumber = (val: number | null) => {
    if (val === null) return '—';
    return val.toLocaleString('ru-RU');
  };

  const toggleTable = (ti: number) => {
    setOpenTables((prev) => {
      const next = new Set(prev);
      if (next.has(ti)) next.delete(ti);
      else next.add(ti);
      return next;
    });
  };

  const toggleDefault = (ti: number, checked: boolean) => {
    setOpenTables((prev) => {
      const next = new Set(prev);
      if (checked) next.add(ti);
      else next.delete(ti);
      localStorage.setItem('homeDefaultOpen', JSON.stringify(Array.from(next)));
      return next;
    });
  };

  // «Расходы по ТБ» и «Административные расходы» должны совпадать с итогом своей расшифровки.
  // Раскладываем сверки по именам строк, чтобы показать итог прямо под строкой.
  const checkByRow = new Map<string, typeof checks[number]>();
  for (const c of checks) {
    for (const a of c.anchor) checkByRow.set(a, c);
  }

  const toggleRow = (key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <div className="report">
      <div className="cluster-selector">
        <label>Кластер: </label>
        <select
          value={selectedCluster}
          onChange={(e) => setSelectedCluster(e.target.value)}
        >
          {clusters.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        <label style={{ marginLeft: '1rem' }}>Конфиг: </label>
        <select
          value={selectedConfig}
          onChange={(e) => setSelectedConfig(e.target.value)}
        >
          {configs.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        {isCustom && <button className="config-reset-btn" onClick={resetConfig} title="Сбросить к исходному">Сброс</button>}
        {loading && <span className="loading-indicator">Загрузка...</span>}
      </div>
      {(checks.length > 0 || months !== null) && (
        <div className="report-checks">
          {checks.map((c) => (
            <div key={c.table} className={`check-badge${c.ok ? ' is-ok' : c.incomplete ? ' is-unknown' : ' is-bad'}`}
                 title={`${c.table}: сумма ${c.table_sum.toLocaleString('ru-RU')} против «${c.anchor.join(' + ')}» ${c.anchor_value.toLocaleString('ru-RU')}`}>
              <span className="check-mark">{c.ok ? '✓' : c.incomplete ? '?' : '!'}</span>
              <span className="check-name">{c.table}</span>
              <span className="check-value">
                {c.ok ? 'сходится' : c.incomplete ? 'нет данных для сверки'
                  : `расхождение ${c.diff.toLocaleString('ru-RU', { maximumFractionDigits: 2 })}`}
              </span>
            </div>
          ))}
          {months !== null && (
            <div className="check-badge is-info" title="Задаётся на странице «Сводный» → Настройки. Влияет на вычет зарплаты во всех отчётах.">
              <span className="check-mark">⏱</span>
              <span className="check-name">Период отчёта</span>
              <span className="check-value">{months} мес.</span>
            </div>
          )}
        </div>
      )}
      {warnings.length > 0 && (
        <div className="report-warnings">
          <strong>Не найдено значений ({warnings.length}):</strong>
          <ul>
            {warnings.map((w, i) => (
              <li key={i}>
                <span className="warning-table">{w.table}</span>
                {' → '}{w.row}{w.detail ? ` → ${w.detail}` : ''}
                <span className="warning-source">{w.source}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {tables.map((table, ti) => {
        const allRows = table.sections.flatMap((section) =>
          section.rows.map((row) => ({ ...row }))
        );
        return (
          <table key={ti} className="report-table">
            <thead>
              <tr className="table-toggle" onClick={() => toggleTable(ti)}>
                <th>
                  <input
                    type="checkbox"
                    className="table-default-checkbox"
                    checked={openTables.has(ti)}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => toggleDefault(ti, e.target.checked)}
                    title="Открыт по умолчанию"
                  />
                  <span className="table-toggle-arrow">{openTables.has(ti) ? '▼' : '▶'}</span>
                  {table.name}
                </th>
                <th>Сумма</th>
                <th>Источник</th>
              </tr>
            </thead>
            {openTables.has(ti) && (
              <tbody>
                {allRows.map((row, i) => {
                  const rowKey = `${ti}-${i}`;
                  const check = checkByRow.get(row.name);
                  return (
                    <React.Fragment key={i}>
                      <tr
                        className={[
                          row.bold ? 'row-bold' : '',
                          row.color ? `row-${row.color}` : '',
                          row.details && row.details.length > 0 ? 'row-expandable' : '',
                          check ? (check.ok ? 'row-tied' : 'row-untied') : '',
                        ].join(' ').trim()}
                        onClick={() => row.details && row.details.length > 0 && toggleRow(rowKey)}
                      >
                        <td>
                          {row.name}
                          {getConfigRowType(ti, i) === 'multi_lookup' && (
                            <button className="row-edit-btn" title="Редактировать источники" onClick={(e) => {
                              e.stopPropagation();
                              const info = getConfigRowInfo(ti, i);
                              if (info) openRowEditor(ti, info.si, info.ri, info.row);
                            }}>&#9998;</button>
                          )}
                        </td>
                        <td className="num">
                          {formatNumber(row.value)}
                          {check && (
                            <span className={`row-check${check.ok ? ' is-ok' : ' is-bad'}`}>
                              расшифровка: {formatNumber(check.table_sum)}
                              {!check.ok && ` (${check.diff > 0 ? '+' : '−'}${formatNumber(Math.abs(check.diff))})`}
                            </span>
                          )}
                        </td>
                        <td className="source">
                          {row.source || '—'}
                          {check && (
                            <span className="row-check-note">
                              {check.table}
                              {check.anchor.length > 1 ? ` · вместе с «${check.anchor.filter((a) => a !== row.name).join(', ')}»` : ''}
                            </span>
                          )}
                        </td>
                      </tr>
                      {expanded.has(rowKey) && row.details && (() => {
                        const rows: React.ReactNode[] = [];
                        if (row.bold) {
                          rows.push(
                            <tr key={`${rowKey}-hdr`} className="row-detail-header">
                              <td className="detail-name">Строка</td>
                              <td className="num detail-col-label" colSpan={2}>Значение</td>
                            </tr>
                          );
                          row.details!.forEach((d, j) => {
                            rows.push(
                              <tr key={`${rowKey}-${j}`} className={`row-detail${d.sign && d.sign > 0 ? ' detail-positive' : ''}${d.sign && d.sign < 0 ? ' detail-negative' : ''}`}>
                                <td className="detail-name">{d.name}</td>
                                <td className="num" colSpan={2}>{formatNumber(d.value)}</td>
                              </tr>
                            );
                          });
                        } else {
                          rows.push(
                            <tr key={`${rowKey}-hdr`} className="row-detail-header">
                              <td className="detail-name">Субконто</td>
                              <td className="num detail-col-label">Дебет</td>
                              <td className="num detail-col-label">Кредит</td>
                            </tr>
                          );
                          let lastAfter: string | undefined;
                          row.details!.forEach((d, j) => {
                            if (d.after && d.after !== lastAfter) {
                              lastAfter = d.after;
                              rows.push(
                                <tr key={`${rowKey}-g-${j}`} className="row-detail-group">
                                  <td className="detail-group-name" colSpan={3}>{d.after}</td>
                                </tr>
                              );
                            }
                            const detailKey = `${rowKey}-${j}`;
                            const hasSubDetails = d.sub_details && d.sub_details.length > 0;
                            rows.push(
                              <tr key={detailKey} className={`row-detail${d.sign && d.sign > 0 ? ' detail-positive' : ''}${d.sign && d.sign < 0 ? ' detail-negative' : ''}${hasSubDetails ? ' detail-expandable' : ''}`}
                                onClick={hasSubDetails ? (e) => { e.stopPropagation(); toggleRow(detailKey); } : undefined}>
                                <td className={d.after ? 'detail-name detail-indented' : 'detail-name'}>{d.name}{hasSubDetails ? ` (${d.sub_details!.length})` : ''}</td>
                                <td className="num">{d.column === 'Дебет' ? formatNumber(d.value) : '—'}</td>
                                <td className="num">{d.column === 'Кредит' ? formatNumber(d.value) : '—'}</td>
                              </tr>
                            );
                            if (hasSubDetails && expanded.has(detailKey)) {
                              d.sub_details!.forEach((sd, k) => {
                                rows.push(
                                  <tr key={`${detailKey}-sub-${k}`} className="row-sub-detail">
                                    <td className="detail-name sub-detail-name">{d.name} #{k + 1}</td>
                                    <td className="num">{d.column === 'Дебет' ? formatNumber(sd.value) : '—'}</td>
                                    <td className="num">{d.column === 'Кредит' ? formatNumber(sd.value) : '—'}</td>
                                  </tr>
                                );
                              });
                            }
                          });
                        }
                        return <>{rows}</>;
                      })()}
                    </React.Fragment>
                  );
                })}
                {table.name.includes('Расшифровка') && (
                  <tr className="row-total">
                    <td><strong>Итого</strong></td>
                    <td className="num"><strong>{formatNumber(allRows.reduce((sum, r) => sum + (r.value || 0), 0))}</strong></td>
                    <td className="source">—</td>
                  </tr>
                )}
              </tbody>
            )}
          </table>
        );
      })}

      {editingRow && (
        <>
          <div className="panel-overlay" onClick={() => setEditingRow(null)} />
          <div className="detail-panel config-editor-panel">
            <div className="detail-panel-header">
              <h3>{editingRow.name}</h3>
              <span className="detail-panel-label">Источники</span>
              <button className="detail-panel-close" onClick={() => setEditingRow(null)}>✕</button>
            </div>

            <div className="config-lookups-list">
              {editingRow.lookups.map((lk, idx) => (
                <div key={idx} className={`config-lookup-item${lk.type === 'static' ? ' config-lookup-static' : ''}`}>
                  <div className="config-lookup-info">
                    {lk.type === 'static' ? (
                      <span><strong>Статичное:</strong> {(lk as any).name} = {(lk as any).value}</span>
                    ) : (
                      <>
                        <span className="config-lookup-file">{lk.file}</span>
                        <span className="config-lookup-account">{lk.account}</span>
                        {lk.after && <span className="config-lookup-after">после: {lk.after}</span>}
                        <span className="config-lookup-col">{lk.group} / {lk.sub}</span>
                        <span className={`config-lookup-sign ${lk.sign === -1 ? 'sign-minus' : 'sign-plus'}`}>
                          {lk.sign === -1 ? '−' : '+'}
                        </span>
                      </>
                    )}
                  </div>
                  {lk.type !== 'static' && (
                    <button className="cluster-delete" onClick={() => removeLookup(idx)} title="Удалить">&times;</button>
                  )}
                </div>
              ))}
            </div>

            <div className="config-add-lookup">
              <h4>Добавить источник</h4>
              <div className="config-add-form">
                <select value={newLkFile} onChange={(e) => { setNewLkFile(e.target.value); loadAccounts(e.target.value); }}>
                  <option value="">Файл...</option>
                  {['7111', '7211', '7470', 'osv', 'sole_7111', 'sole_7211', 'sole_7212'].map((f) => (
                    <option key={f} value={f}>{f}</option>
                  ))}
                </select>
                <select value={newLkAccount} onChange={(e) => setNewLkAccount(e.target.value)}>
                  <option value="">Счёт...</option>
                  {accounts.map((a, i) => (
                    <option key={i} value={a.name}>{a.name}</option>
                  ))}
                </select>
                <input type="text" placeholder="После (after)" value={newLkAfter} onChange={(e) => setNewLkAfter(e.target.value)} />
                <select value={newLkGroup} onChange={(e) => setNewLkGroup(e.target.value)}>
                  <option value="Оборот за период">Оборот за период</option>
                  <option value="Обороты за период">Обороты за период</option>
                  <option value="Сальдо на конец периода">Сальдо на конец периода</option>
                  <option value="Сальдо на начало периода">Сальдо на начало периода</option>
                </select>
                <select value={newLkSub} onChange={(e) => setNewLkSub(e.target.value)}>
                  <option value="Дебет">Дебет</option>
                  <option value="Кредит">Кредит</option>
                </select>
                <select value={newLkSign} onChange={(e) => setNewLkSign(Number(e.target.value))}>
                  <option value={1}>+ (плюс)</option>
                  <option value={-1}>− (минус)</option>
                </select>
                <button onClick={addLookup} disabled={!newLkFile || !newLkAccount}>Добавить</button>
              </div>
            </div>

            <div className="config-editor-actions">
              <button onClick={saveLookups} disabled={savingLookups}>{savingLookups ? 'Сохранение...' : 'Сохранить'}</button>
              <button onClick={() => setEditingRow(null)}>Отмена</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
